// src/pages/Production/Recipes.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    searchContainer: {
        marginBottom: '24px'
    },
    searchInputContainer: {
        position: 'relative',
        maxWidth: '400px'
    },
    searchIcon: {
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        width: '16px',
        height: '16px'
    },
    searchInput: {
        padding: '12px 16px 12px 44px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        width: '100%',
        background: '#f8fafc',
        outline: 'none',
        transition: 'all 0.2s'
    },
    tableContainer: {
        maxHeight: '60vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    trHoverActive: {
        background: '#f8fafc'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#64748b'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100
    },
    modalContent: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '800px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px'
    },
    formGroup: { marginBottom: '20px' },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s',
        minHeight: '100px',
        resize: 'vertical'
    },
    badge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        marginLeft: '8px'
    },
    activeBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },
    draftBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e'
    },
    versionBadge: {
        backgroundColor: '#dbeafe',
        color: '#1e40af'
    }
};

function AddRecipeModal({ onClose, onSave, suppliers, products, initialData }) {
    const [formData, setFormData] = useState(initialData || {
        name: '',
        product: '',
        version: 'v1.0',
        status: 'active',
        stages: [
            {
                id: Date.now().toString(),
                name: 'Üretim Aşaması 1',
                description: '',
                sequence: 1,
                type: 'internal',
                duration: 0,
                workCenters: [],
                subcontractors: [],
                createsSemiFinishedProduct: false,
                inputMaterials: [
                    {
                        id: Date.now().toString() + '-mat',
                        material: '',
                        quantity: 1,
                        unit: 'Adet'
                    }
                ],
                outputProduct: {
                    name: '',
                    quantity: 1,
                    unit: 'Adet'
                }
            }
        ]
    });

    const [availableWorkCenters, setAvailableWorkCenters] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isMaterialDropdownOpen, setIsMaterialDropdownOpen] = useState({});
    const [materialSearchTerms, setMaterialSearchTerms] = useState({});
    const [expandedStage, setExpandedStage] = useState('all'); // Default to all expanded

    useEffect(() => {
        // In a real implementation, we would fetch work centers from the database
        setAvailableWorkCenters([
            { id: 'wc1', name: 'Dikim Atölyesi' },
            { id: 'wc2', name: 'Boyama Bölümü' },
            { id: 'wc3', name: 'Kalite Kontrol' },
            { id: 'wc4', name: 'Paketleme' }
        ]);
    }, []);

    const calculateEstimatedCost = () => {
        let total = 0;
        formData.stages.forEach(stage => {
            // Labor Cost
            if (stage.type === 'subcontracting' && stage.subcontractors) {
                stage.subcontractors.forEach(sub => {
                    total += parseFloat(sub.laborCost || 0);
                });
            }

            // Material Cost
            if (stage.inputMaterials) {
                stage.inputMaterials.forEach(mat => {
                    const productModel = products.find(p => p.name === mat.material);
                    const price = parseFloat(productModel?.purchasePrice || productModel?.price || 0);
                    total += (parseFloat(mat.quantity || 0) * price);
                });
            }
        });
        return total;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleStageChange = (stageIndex, field, value) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            newStages[stageIndex] = { ...newStages[stageIndex], [field]: value };
            return { ...prev, stages: newStages };
        });
    };

    const handleStageMaterialChange = (stageIndex, materialIndex, field, value) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            const newMaterials = [...newStages[stageIndex].inputMaterials];
            newMaterials[materialIndex] = { ...newMaterials[materialIndex], [field]: value };
            newStages[stageIndex] = { ...newStages[stageIndex], inputMaterials: newMaterials };
            return { ...prev, stages: newStages };
        });
    };

    const handleOutputProductChange = (stageIndex, field, value) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                outputProduct: { ...newStages[stageIndex].outputProduct, [field]: value }
            };
            return { ...prev, stages: newStages };
        });
    };

    const handleSubcontractorChange = (stageIndex, supplierId) => {
        const supplier = suppliers.find(s => s.id === supplierId);
        setFormData(prev => {
            const newStages = [...prev.stages];
            const currentSubcontractors = newStages[stageIndex].subcontractors || [];
            if (supplier && !currentSubcontractors.some(s => s.supplierId === supplierId)) {
                newStages[stageIndex] = {
                    ...newStages[stageIndex],
                    subcontractors: [
                        ...currentSubcontractors,
                        {
                            supplierId: supplier.id,
                            supplierName: supplier.name,
                            laborCost: 0
                        }
                    ]
                };
            }
            return { ...prev, stages: newStages };
        });
    };

    const handleSubcontractorLaborCostChange = (stageIndex, supplierId, laborCost) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            const currentSubcontractors = newStages[stageIndex].subcontractors || [];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                subcontractors: currentSubcontractors.map(sub =>
                    sub.supplierId === supplierId
                        ? { ...sub, laborCost: parseFloat(laborCost) || 0 }
                        : sub
                )
            };
            return { ...prev, stages: newStages };
        });
    };

    const removeSubcontractor = (stageIndex, supplierId) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            const currentSubcontractors = newStages[stageIndex].subcontractors || [];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                subcontractors: currentSubcontractors.filter(s => s.supplierId !== supplierId)
            };
            return { ...prev, stages: newStages };
        });
    };

    const addStage = () => {
        const newStageId = Date.now().toString();
        setFormData(prev => ({
            ...prev,
            stages: [
                ...prev.stages,
                {
                    id: newStageId,
                    name: `Üretim Aşaması ${prev.stages.length + 1}`,
                    description: '',
                    sequence: prev.stages.length + 1,
                    type: 'internal',
                    duration: 0,
                    workCenters: [],
                    subcontractors: [],
                    createsSemiFinishedProduct: false,
                    inputMaterials: [
                        {
                            id: Date.now().toString() + '-mat',
                            material: '',
                            quantity: 1,
                            unit: 'Adet'
                        }
                    ],
                    outputProduct: {
                        name: '',
                        quantity: 1,
                        unit: 'Adet'
                    }
                }
            ]
        }));
        setExpandedStage(newStageId);
    };

    const removeStage = (stageIndex) => {
        if (formData.stages.length <= 1) return;
        setFormData(prev => ({
            ...prev,
            stages: prev.stages.filter((_, i) => i !== stageIndex)
        }));
    };

    const addMaterialToStage = (stageIndex) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                inputMaterials: [
                    ...newStages[stageIndex].inputMaterials,
                    {
                        id: Date.now().toString() + '-mat',
                        material: '',
                        quantity: 1,
                        unit: 'Adet'
                    }
                ]
            };
            return { ...prev, stages: newStages };
        });
    };

    const removeMaterialFromStage = (stageIndex, materialIndex) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                inputMaterials: newStages[stageIndex].inputMaterials.filter((_, i) => i !== materialIndex)
            };
            return { ...prev, stages: newStages };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleProductSelect = (productName) => {
        setFormData(prev => ({ ...prev, product: productName }));
        setIsDropdownOpen(false);
        setSearchTerm('');
    };

    const handleMaterialSelect = (stageIndex, materialIndex, materialName) => {
        handleStageMaterialChange(stageIndex, materialIndex, 'material', materialName);
        setIsMaterialDropdownOpen(prev => ({ ...prev, [`${stageIndex}-${materialIndex}`]: false }));
        setMaterialSearchTerms(prev => ({ ...prev, [`${stageIndex}-${materialIndex}`]: '' }));
    };

    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

    const toggleMaterialDropdown = (stageIndex, materialIndex) => {
        const key = `${stageIndex}-${materialIndex}`;
        setIsMaterialDropdownOpen(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const filteredProducts = products.filter(product => {
        const term = searchTerm.toLowerCase();
        return product.name.toLowerCase().includes(term) ||
            (product.barcode && product.barcode.toLowerCase().includes(term));
    });

    const getFilteredMaterials = (stageIndex, materialIndex) => {
        const searchTerm = materialSearchTerms[`${stageIndex}-${materialIndex}`] || '';
        const term = searchTerm.toLowerCase();
        return products.filter(product =>
            product.isRawMaterial &&
            (product.name.toLowerCase().includes(term) ||
                (product.barcode && product.barcode.toLowerCase().includes(term)))
        );
    };

    const estimatedCost = calculateEstimatedCost();

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Yeni Reçete Oluştur</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={{ ...styles.formGroup, background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontWeight: '600', color: '#374151' }}>Özet Bilgiler</div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>
                                Aşama Sayısı: <span style={{ color: '#0f172a', fontWeight: 'bold' }}>{formData.stages.length}</span>
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>
                                Tahmini Maliyet: <span style={{ color: '#06C167', fontWeight: 'bold' }}>{estimatedCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Reçete Adı *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                placeholder="Reçete adı girin"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Ürün *</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    value={formData.product}
                                    onChange={(e) => {
                                        setFormData(prev => ({ ...prev, product: e.target.value }));
                                        setSearchTerm(e.target.value);
                                        setIsDropdownOpen(true);
                                    }}
                                    onFocus={() => setIsDropdownOpen(true)}
                                    placeholder="Ürün adı veya barkod ara"
                                    style={styles.input}
                                    autoComplete="off"
                                />
                                {isDropdownOpen && (
                                    <div style={{
                                        position: 'absolute', top: '100%', left: 0, right: 0, background: 'white',
                                        border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        zIndex: 1000, maxHeight: '200px', overflowY: 'auto', marginTop: '4px'
                                    }}>
                                        {filteredProducts.map(product => (
                                            <div
                                                key={product.id}
                                                onClick={() => handleProductSelect(product.name)}
                                                style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                            >
                                                <span>{product.name}</span>
                                                {product.barcode && (
                                                    <span style={{ fontSize: '12px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                                        {product.barcode}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Versiyon</label>
                            <input
                                type="text"
                                name="version"
                                value={formData.version}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="Birden fazla versiyon (örn: v1.0)"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Durum</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                style={styles.select}
                            >
                                <option value="active">Aktif</option>
                                <option value="draft">Taslak</option>
                                <option value="archived">Arşivlenmiş</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '30px', marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                            Üretim Aşamaları
                        </h3>
                        {formData.stages.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setExpandedStage(expandedStage ? null : 'all')}
                                style={{ background: 'none', border: 'none', color: '#06C167', cursor: 'pointer', fontWeight: '500' }}
                            >
                                {expandedStage === 'all' ? 'Tümünü Kapat' : 'Tümünü Genişlet'}
                            </button>
                        )}
                    </div>

                    {formData.stages.map((stage, stageIndex) => {
                        const isExpanded = expandedStage === 'all' || expandedStage === stage.id;
                        return (
                            <div key={stage.id} style={{
                                border: '1px solid #e2e8f0', borderRadius: '12px', marginBottom: '20px', background: 'white', overflow: 'hidden'
                            }}>
                                <div
                                    onClick={() => setExpandedStage(isExpanded ? null : stage.id)}
                                    style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '15px 20px', cursor: 'pointer', background: isExpanded ? '#f8fafc' : 'white',
                                        borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{stage.name}</h4>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {formData.stages.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); removeStage(stageIndex); }}
                                                style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}
                                            >
                                                Sil
                                            </button>
                                        )}
                                        <span style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div style={{ padding: '20px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Aşama Adı</label>
                                                <input
                                                    type="text"
                                                    value={stage.name}
                                                    onChange={(e) => handleStageChange(stageIndex, 'name', e.target.value)}
                                                    style={styles.input}
                                                />
                                            </div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Sıra</label>
                                                <input
                                                    type="number"
                                                    value={stage.sequence}
                                                    onChange={(e) => handleStageChange(stageIndex, 'sequence', parseInt(e.target.value))}
                                                    style={styles.input}
                                                />
                                            </div>
                                        </div>

                                        <div style={styles.formGroup}>
                                            <label style={styles.label}>Açıklama</label>
                                            <textarea
                                                value={stage.description}
                                                onChange={(e) => handleStageChange(stageIndex, 'description', e.target.value)}
                                                style={styles.textarea}
                                            />
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Tür</label>
                                                <select
                                                    value={stage.type}
                                                    onChange={(e) => handleStageChange(stageIndex, 'type', e.target.value)}
                                                    style={styles.select}
                                                >
                                                    <option value="internal">İç Üretim</option>
                                                    <option value="subcontracting">Fason</option>
                                                </select>
                                            </div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Tahmini Süre (dk)</label>
                                                <input
                                                    type="number"
                                                    value={stage.duration}
                                                    onChange={(e) => handleStageChange(stageIndex, 'duration', parseInt(e.target.value) || 0)}
                                                    style={styles.input}
                                                />
                                            </div>
                                        </div>

                                        {stage.type === 'subcontracting' && (
                                            <div style={{ background: '#f1f5f9', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                                                <label style={{ ...styles.label, marginBottom: '10px' }}>Fason Tedarikçiler</label>
                                                {stage.subcontractors && stage.subcontractors.map(sub => (
                                                    <div key={sub.supplierId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', background: 'white', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                        <span>{sub.supplierName}</span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <input
                                                                type="number"
                                                                value={sub.laborCost}
                                                                onChange={(e) => handleSubcontractorLaborCostChange(stageIndex, sub.supplierId, e.target.value)}
                                                                style={{ ...styles.input, width: '100px' }}
                                                                placeholder="İşçilik Maliyeti"
                                                            />
                                                            <span>₺</span>
                                                            <button type="button" onClick={() => removeSubcontractor(stageIndex, sub.supplierId)} style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}>×</button>
                                                        </div>
                                                    </div>
                                                ))}
                                                <select
                                                    onChange={(e) => {
                                                        if (e.target.value) handleSubcontractorChange(stageIndex, e.target.value);
                                                    }}
                                                    value=""
                                                    style={styles.select}
                                                >
                                                    <option value="">+ Tedarikçi Ekle</option>
                                                    {suppliers.filter(s => !stage.subcontractors?.some(sub => sub.supplierId === s.id)).map(s => (
                                                        <option key={s.id} value={s.id}>{s.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div style={{ marginTop: '20px' }}>
                                            <label style={{ ...styles.label, borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '15px' }}>Girdi Hammaddeleri</label>
                                            {stage.inputMaterials.map((material, mIndex) => {
                                                const productModel = products.find(p => p.name === material.material);
                                                const price = parseFloat(productModel?.purchasePrice || productModel?.price || 0);
                                                const total = (parseFloat(material.quantity || 0) * price);

                                                return (
                                                    <div key={material.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                                                        <div style={{ position: 'relative' }}>
                                                            <input
                                                                type="text"
                                                                value={material.material}
                                                                onChange={(e) => {
                                                                    handleStageMaterialChange(stageIndex, mIndex, 'material', e.target.value);
                                                                    setMaterialSearchTerms(prev => ({ ...prev, [`${stageIndex}-${mIndex}`]: e.target.value }));
                                                                    setIsMaterialDropdownOpen(prev => ({ ...prev, [`${stageIndex}-${mIndex}`]: true }));
                                                                }}
                                                                onFocus={() => setIsMaterialDropdownOpen(prev => ({ ...prev, [`${stageIndex}-${mIndex}`]: true }))}
                                                                placeholder="Hammadde Ara"
                                                                style={styles.input}
                                                            />
                                                            {isMaterialDropdownOpen[`${stageIndex}-${mIndex}`] && (
                                                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', zIndex: 10, maxHeight: '150px', overflowY: 'auto' }}>
                                                                    {getFilteredMaterials(stageIndex, mIndex).map(p => (
                                                                        <div key={p.id} onClick={() => handleMaterialSelect(stageIndex, mIndex, p.name)} style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>{p.name}</div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <input type="number" value={material.quantity} onChange={e => handleStageMaterialChange(stageIndex, mIndex, 'quantity', e.target.value)} style={styles.input} placeholder="Miktar" />
                                                        <select value={material.unit} onChange={e => handleStageMaterialChange(stageIndex, mIndex, 'unit', e.target.value)} style={styles.select}>
                                                            <option value="Adet">Adet</option><option value="Kg">Kg</option><option value="Litre">Lt</option><option value="Metre">Mt</option>
                                                        </select>
                                                        <div style={{ fontSize: '13px', color: '#64748b', textAlign: 'right' }}>
                                                            {total > 0 ? `${total.toLocaleString('tr-TR')} ₺` : '-'}
                                                        </div>
                                                        <button type="button" onClick={() => removeMaterialFromStage(stageIndex, mIndex)} style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}>×</button>
                                                    </div>
                                                );
                                            })}
                                            <button type="button" onClick={() => addMaterialToStage(stageIndex)} style={{ ...styles.button, background: '#f1f5f9', color: '#374151', padding: '6px 12px', fontSize: '13px' }}>+ Hammadde Ekle</button>
                                        </div>

                                        <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                                            <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <input type="checkbox" checked={stage.createsSemiFinishedProduct} onChange={e => handleStageChange(stageIndex, 'createsSemiFinishedProduct', e.target.checked)} />
                                                Yarı Mamül Oluşturur
                                            </label>
                                            {stage.createsSemiFinishedProduct && (
                                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginTop: '10px' }}>
                                                    <input type="text" value={stage.outputProduct.name} onChange={e => handleOutputProductChange(stageIndex, 'name', e.target.value)} style={styles.input} placeholder="Çıktı Ürün Adı" />
                                                    <input type="number" value={stage.outputProduct.quantity} onChange={e => handleOutputProductChange(stageIndex, 'quantity', e.target.value)} style={styles.input} placeholder="Miktar" />
                                                    <select value={stage.outputProduct.unit} onChange={e => handleOutputProductChange(stageIndex, 'unit', e.target.value)} style={styles.select}>
                                                        <option value="Adet">Adet</option><option value="Kg">Kg</option><option value="Litre">Lt</option><option value="Metre">Mt</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <button type="button" onClick={addStage} style={{ ...styles.button, background: '#f8fafc', color: '#374151', border: '1px dashed #cbd5e1', width: '100%', marginBottom: '20px' }}>+ Yeni Aşama Ekle</button>

                    <div style={styles.modalActions}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: '#f1f5f9', color: '#374151' }}>İptal</button>
                        <button type="submit" style={styles.button}>Reçeteyi Kaydet</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const StatCard = ({ title, value, icon, color, subValue, onClick }) => (
    <div
        onClick={onClick}
        style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0',
            cursor: onClick ? 'pointer' : 'default',
            transition: 'transform 0.2s',
        }}
        onMouseEnter={(e) => onClick && (e.currentTarget.style.transform = 'translateY(-3px)')}
        onMouseLeave={(e) => onClick && (e.currentTarget.style.transform = 'translateY(0)')}
    >
        <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            background: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            marginRight: '20px',
            color: color
        }}>
            {icon}
        </div>
        <div>
            <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '6px' }}>{title}</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '2px' }}>{value}</div>
            {subValue && <div style={{ fontSize: '12px', color: '#94a3b8' }}>{subValue}</div>}
        </div>
    </div>
);

function Recipes() {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [recipes, setRecipes] = useState([]);
    const [subcontractors, setSubcontractors] = useState([]);
    const [products, setProducts] = useState([]); // This will now contain all products
    const [finishedProducts, setFinishedProducts] = useState([]); // This will contain only finished products for the table
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        draft: 0,
        products: 0
    });

    const [modalInitialData, setModalInitialData] = useState(null);

    // Fetch real data from the database
    useEffect(() => {
        if (!userData?.companyId) return;

        const companyId = userData.companyId;

        // Fetch subcontractors
        const unsubSubcontractors = onSnapshot(
            query(collection(db, 'companies', companyId, 'subcontractors')),
            snap => setSubcontractors(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch all products (both raw materials and finished products)
        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            snap => {
                const allProducts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setProducts(allProducts); // Store all products for the modal
                // Filter to show only products that are NOT raw materials (finished products) for the table
                const finishedProducts = allProducts.filter(product => !product.isRawMaterial);
                setFinishedProducts(finishedProducts);
            }
        );

        // Fetch recipes
        const unsubRecipes = onSnapshot(
            query(collection(db, 'companies', companyId, 'recipes')),
            snap => {
                const recipeList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setRecipes(recipeList);

                // Calculate stats
                setStats({
                    total: recipeList.length,
                    active: recipeList.filter(r => r.status === 'active').length,
                    draft: recipeList.filter(r => r.status === 'draft').length,
                    products: new Set(recipeList.map(r => r.product)).size // Unique products with recipes
                });
            }
        );

        return () => {
            unsubSubcontractors();
            unsubProducts();
            unsubRecipes();
        };
    }, [userData]);

    const handleAddRecipe = async (recipeData) => {
        try {
            // Save the recipe to the database
            await addDoc(collection(db, 'companies', userData.companyId, 'recipes'), {
                ...recipeData,
                companyId: userData.companyId,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            setShowAddModal(false);
            setModalInitialData(null);
            alert('Reçete başarıyla kaydedildi!');
        } catch (error) {
            console.error("Error adding recipe:", error);
            alert('Reçete eklenirken bir hata oluştu: ' + error.message);
        }
    };

    const handleDeleteRecipe = async (recipeId, recipeName) => {
        if (!userData?.companyId) return;

        // Confirm deletion
        if (!window.confirm(`"${recipeName}" adlı reçeteyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
            return;
        }

        try {
            // Delete the recipe from the database
            await deleteDoc(doc(db, 'companies', userData.companyId, 'recipes', recipeId));
            alert('Reçete başarıyla silindi!');
        } catch (error) {
            console.error("Error deleting recipe:", error);
            alert('Reçete silinirken bir hata oluştu: ' + error.message);
        }
    };

    const handleDuplicateRecipe = (e, recipe) => {
        e.stopPropagation();

        // Prepare duplicate data
        const { id, createdAt, updatedAt, ...recipeData } = recipe;

        // Generate new IDs for stages and materials to ensure they are treated as new items
        const newStages = (recipeData.stages || []).map(stage => ({
            ...stage,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            inputMaterials: (stage.inputMaterials || []).map(mat => ({
                ...mat,
                id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '-mat'
            }))
        }));

        const newRecipe = {
            ...recipeData,
            name: `${recipeData.name} - Kopya`,
            status: 'draft',
            version: 'v1.0',
            stages: newStages
        };

        setModalInitialData(newRecipe);
        setShowAddModal(true);
    };

    const filteredRecipes = (recipes || []).filter(recipe =>
        recipe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (recipe.product && recipe.product.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span style={{ ...styles.badge, ...styles.activeBadge }}>Aktif</span>;
            case 'draft':
                return <span style={{ ...styles.badge, ...styles.draftBadge }}>Taslak</span>;
            case 'archived':
                return <span style={{ ...styles.badge, ...styles.draftBadge }}>Arşivlenmiş</span>;
            default:
                return null;
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Üretim Reçeteleri</h1>
                    <p style={{ color: '#64748b', marginTop: '4px' }}>
                        Ürün üretim reçetelerini tanımlayın ve yönetin
                    </p>
                </div>
                <div style={styles.headerActions}>
                    <button
                        onClick={() => {
                            setModalInitialData(null);
                            setShowAddModal(true);
                        }}
                        style={styles.button}
                    >
                        + Yeni Reçete
                    </button>
                </div>
            </div>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
            }}>
                <StatCard
                    title="Toplam Reçete"
                    value={stats.total}
                    icon="📋"
                    color="#06C167"
                    subValue="Sistemdeki tüm reçeteler"
                />
                <StatCard
                    title="Aktif Reçete"
                    value={stats.active}
                    icon="✅"
                    color="#10b981"
                    subValue="Kullanılabilir durumda"
                />
                <StatCard
                    title="Taslak Reçete"
                    value={stats.draft}
                    icon="📝"
                    color="#f59e0b"
                    subValue="Düzenleme aşamasında"
                />
                <StatCard
                    title="Reçeteli Ürün"
                    value={stats.products}
                    icon="📦"
                    color="#3b82f6"
                    subValue="Reçetesi olan ürün sayısı"
                />
            </div>

            <div style={styles.box}>
                <div style={styles.searchContainer}>
                    <div style={styles.searchInputContainer}>
                        <span style={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            placeholder="Reçete veya ürün ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                </div>

                {filteredRecipes.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>📋</div>
                        <h3 style={styles.emptyText}>Reçete Bulunamadı</h3>
                        <p style={styles.emptySubtext}>
                            {searchTerm ? 'Arama kriterlerinize uygun reçete bulunamadı.' : 'Henüz hiç reçete eklenmemiş.'}
                        </p>
                        <button
                            onClick={() => setShowAddModal(true)}
                            style={{ ...styles.button, marginTop: '20px' }}
                        >
                            İlk Reçetenizi Ekleyin
                        </button>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Reçete Adı</th>
                                    <th style={styles.th}>Ürün</th>
                                    <th style={styles.th}>Versiyon</th>
                                    <th style={styles.th}>Aşama Sayısı</th>
                                    <th style={styles.th}>Durum</th>
                                    <th style={styles.th}>Oluşturulma Tarihi</th>
                                    <th style={styles.th}>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRecipes.map(recipe => (
                                    <tr
                                        key={recipe.id}
                                        style={{ ...styles.trHover, ...(hoveredRow === recipe.id ? styles.trHoverActive : {}) }}
                                        onMouseEnter={() => setHoveredRow(recipe.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        onClick={() => navigate(`/uretim/reçeteler/${recipe.id}`)}
                                    >
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '600' }}>{recipe.name}</div>
                                        </td>
                                        <td style={styles.td}>{recipe.product}</td>
                                        <td style={styles.td}>
                                            <span style={{ ...styles.badge, ...styles.versionBadge }}>
                                                {recipe.version}
                                            </span>
                                        </td>
                                        <td style={styles.td}>{(recipe.stages || []).length} aşama</td>
                                        <td style={styles.td}>
                                            {getStatusBadge(recipe.status)}
                                        </td>
                                        <td style={styles.td}>
                                            {recipe.createdAt?.toDate ? recipe.createdAt.toDate().toLocaleDateString('tr-TR') :
                                                recipe.createdAt instanceof Date ? recipe.createdAt.toLocaleDateString('tr-TR') :
                                                    'Tarih yok'}
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={(e) => handleDuplicateRecipe(e, recipe)}
                                                    title="Kopyala"
                                                    style={{
                                                        background: '#3b82f6',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '6px 12px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    ❐
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteRecipe(recipe.id, recipe.name);
                                                    }}
                                                    title="Sil"
                                                    style={{
                                                        background: '#ef4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '6px 12px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showAddModal && (
                <AddRecipeModal
                    onClose={() => {
                        setShowAddModal(false);
                        setModalInitialData(null);
                    }}
                    onSave={handleAddRecipe}
                    suppliers={subcontractors}
                    products={products}
                    initialData={modalInitialData}
                />
            )}
        </div>
    );
}

export default Recipes;